import { IMAGES } from "@/lib/products";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Search, Package, Truck, PackageCheck, BookOpen, User, CreditCard, HelpCircle, Cloud, Mail, MessageCircle, Phone, ChevronRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";

const SUPPORT_EMAIL = "your.noteconsole@gmail.com";
const INSTAGRAM_URL = "https://www.instagram.com/note.console?igsh=MTd2MW9hcWh6eG93OQ==";

const TOPICS = [
  { icon: Package, label: "Orders", id: "orders" },
  { icon: Truck, label: "Shipping", id: "shipping" },
  { icon: PackageCheck, label: "Issue Support", id: "returns" },
  { icon: BookOpen, label: "Products", id: "products" },
  { icon: User, label: "Account", id: "account" },
  { icon: HelpCircle, label: "Planner Setup", id: "planner-setup" },
  { icon: Cloud, label: "Digital Companion", id: "digital-companion" },
  { icon: CreditCard, label: "Payments", id: "payments" },
];

const FAQ_TOPICS = [
  {
    id: "orders",
    title: "Orders",
    intro: "Everything about placing, checking, and managing your Note Console order.",
    items: [
      { q: "How do I know my order is confirmed?", a: "After checkout, you will see an order confirmation and receive the order details by email. If you created an account, the order will also appear in your dashboard." },
      { q: "Where can I track my order?", a: "You can track your order from the Orders section in your dashboard. Tracking details are added after the planner is packed and shipped." },
      { q: "Can I change my address after ordering?", a: "Message us as soon as possible. If the order has not shipped yet, we will try to update the address. Once shipped, Not possible." },
      { q: "Can I cancel an order?", a: "Cancellation is possible only before packing or shipping begins. After dispatch, the order cannot be cancelled because it is already with the delivery partner." },
    ],
  },
  {
    id: "shipping",
    title: "Shipping",
    intro: "Delivery timelines, address details, and courier support.",
    items: [
      { q: "How long does shipping take?", a: "Shipping depends on your location. After the order is shipped, delivery usually takes 7-12 days." },
      { q: "When will I get tracking details?", a: "Tracking is shared once your order is packed and handed to the courier. Updates may take a little time to appear after dispatch." },
      { q: "What details are required for delivery?", a: "Please provide your full name, phone number, email, complete address, city, state, and pincode so the courier can contact you and deliver correctly." },
      { q: "What if my package is delayed?", a: "If your tracking has not moved for several days, send us your order number. We will check with the courier and help you with the next update." },
    ],
  },
  {
    id: "returns",
    title: "Issue Support",
    intro: "Help for wrong product delivery or missing items.",
    items: [
      { q: "Can I return my planner after receiving it?", a: "We can only accept a return if the wrong product is delivered or an item is missing from your order. Change-of-mind returns are not supported." },
      { q: "What should I do if I receive the wrong product?", a: "Contact us within 48 hours of delivery with your order number, package photos, and product photos. We will review it and arrange the correct support." },
      { q: "What if something is missing from my order?", a: "Send your order number and clear unboxing/package photos within 48 hours. We will verify the issue and help with the missing item." },
      { q: "Do you offer exchanges?", a: "Exchange support is available only for wrong product delivery or verified missing-item issues." },
    ],
  },
  {
    id: "products",
    title: "Products",
    intro: "Planner format, binding, paper, and what is included.",
    items: [
      { q: "What is inside The Life OS Planner?", a: "It includes planning, reflection, daily dashboard, weekly reset, habit, money, mood, goal, and life audit sections, plus many more guided pages." },
      { q: "Is the planner dated?", a: "The planner is undated, so you can start anytime without wasting pages." },
      { q: "Which binding options are available?", a: "We provide Soft Paperback and Hard Binding options, depending on the selected product variant." },
      { q: "Is it suitable for students and professionals?", a: "Yes. The planner is designed for daily planning, focus, study, business, routines, goals, and personal reflection." },
    ],
  },
  {
    id: "account",
    title: "Account",
    intro: "Login, dashboard, and customer details.",
    items: [
      { q: "Do I need an account to order?", a: "You can browse without an account, but creating one helps you view orders, invoices, and digital access in one place." },
      { q: "Why do you ask for my phone number and email?", a: "We use them to confirm your order, send updates, contact you for delivery issues, and provide support if anything goes wrong." },
      { q: "Can I update my profile details?", a: "You can update details during checkout. For order-related changes after purchase, message us quickly with your order number." },
      { q: "I forgot my login details. What should I do?", a: "Use the login recovery option if available, or contact us with the email used for your order." },
    ],
  },
  {
    id: "planner-setup",
    title: "Planner Setup",
    intro: "How to start using the planner without overthinking it.",
    items: [
      { q: "How should I start the planner?", a: "Start with your vision and current priorities, then move into weekly and daily pages. You do not need to fill every page perfectly on day one." },
      { q: "Do I have to use every section?", a: "Use the sections that match your current situation. The planner is built to support clarity." },
      { q: "How often should I review my planner?", a: "A quick daily check and one weekly reset works well for most people. Monthly reviews help you notice patterns and progress." },
      { q: "Can beginners use it?", a: "Yes,Ofcourse." },
    ],
  },
  {
    id: "digital-companion",
    title: "Digital Companion",
    intro: "Digital extras included with the planner.",
    items: [
      { q: "Is the digital companion included?", a: "Yes, it is complimentary with every planner where mentioned.A standard plan will be provided" },
      { q: "What does it help with?", a: "It helps you keep planner-related notes, references, and important reflections easier to access across your devices." },
      { q: "How do I access it?", a: "After purchase, access details are shared by email or made available through your account/dashboard when enabled." },
      { q: "Can I share it everywhere?", a: "You can use it for your own planning across supported devices. Please do not resell or redistribute the digital files." },
    ],
  },
  {
    id: "payments",
    title: "Payments",
    intro: "Payment methods, invoices, and order charges.",
    items: [
      { q: "Which payment methods are accepted?", a: "Available prepaid payment methods appear during checkout. You can complete payment using the online options shown for your order." },
      { q: "Where can I get my invoice?", a: "If invoices are enabled for your order, you can download them from the Orders section or request help with your order number." },
      { q: "What if my payment fails?", a: "If money is deducted but the order is not confirmed, contact us with your payment screenshot, email, phone number, and order attempt details." },
    ],
  },
];

const FAQS = FAQ_TOPICS.flatMap((topic) => topic.items);

export default function Help() {
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", order: "", subject: "orders", message: "" });

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = FAQS.filter((f) => f.q.toLowerCase().includes(normalizedSearch) || f.a.toLowerCase().includes(normalizedSearch));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete required fields");
    try {
      await addDoc(collection(db, "contact_requests"), {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        orderNumber: form.order.trim(),
        subject: form.subject,
        message: form.message.trim(),
        status: "new",
        source: "help_contact_form",
        page: window.location.pathname,
        createdAt: serverTimestamp(),
      });
      toast.success("Message sent - we'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", order: "", subject: "orders", message: "" });
    } catch (error) {
      console.error(error);
      toast.error("Could not send message. Please email us directly.");
    }
  };

  return (
    <div data-testid="help-page">
      <section className="bg-ivory">
        <div className="container-nc py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline mb-4">We're here to help</p>
            <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-tight">
              Help Center
            </h1>
            <p className="mt-4 text-muted_brown max-w-md">
              Find answers, get support, and connect with our team.
            </p>
            <div className="mt-8 flex items-center border border-line rounded-full bg-cream overflow-hidden max-w-md">
              <Search size={16} className="ml-5 text-muted_brown" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search help articles..."
                data-testid="help-search"
                className="flex-1 bg-transparent px-4 py-3 text-sm outline-none"
              />
              <button className="bg-espresso text-cream px-6 py-3 text-xs uppercase tracking-wider">Search</button>
            </div>
            <div className="mt-5 flex flex-wrap gap-2 text-xs">
              {[
                { label: "Track order", href: "#orders" },
                { label: "Returns", href: "#returns" },
                { label: "Shipping", href: "#shipping" },
                { label: "Digital companion", href: "#digital-companion" },
              ].map((t) => (
                <a key={t.label} href={t.href} className="px-3 py-1 border border-line rounded-full text-muted_brown hover:text-espresso">
                  {t.label}
                </a>
              ))}
            </div>
          </div>
          <img src={IMAGES.planner_flatlay} alt="" className="rounded-2xl w-full aspect-[5/4] object-cover" />
        </div>
      </section>

      <section className="container-nc py-16">
        <h2 className="font-serif text-3xl text-espresso mb-8">Browse by topic</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {TOPICS.map(({ icon: Icon, label, id }) => (
            <a
              href={`#${id}`}
              key={label}
              data-testid={`topic-${label.toLowerCase().replace(/ /g, "-")}`}
              className="p-5 bg-cream border border-line rounded-xl hover:shadow-hover transition-shadow text-left group"
            >
              <Icon size={20} strokeWidth={1.5} className="text-gold mb-4" />
              <div className="text-espresso font-medium">{label}</div>
              <div className="text-xs text-muted_brown mt-1 flex items-center gap-1">
                View articles <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
          ))}
        </div>
      </section>

      <section className="bg-ivory border-y border-line">
        <div className="container-nc py-20 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-12">
          <div>
            <p className="overline mb-4">Frequently asked</p>
            <h2 className="font-serif text-4xl text-espresso leading-tight">
              Questions,
              <br /><span className="italic">answered.</span>
            </h2>
            <p className="text-muted_brown text-sm mt-4">
              Search a question, or browse by topic below.
            </p>
          </div>
          {normalizedSearch ? (
            <Accordion type="single" collapsible className="w-full">
              {filtered.length ? filtered.map((f, i) => (
                <AccordionItem key={`${f.q}-${i}`} value={`search-${i}`} className="border-line">
                  <AccordionTrigger className="text-espresso text-left" data-testid={`help-faq-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted_brown">{f.a}</AccordionContent>
                </AccordionItem>
              )) : (
                <div className="bg-cream border border-line rounded-xl p-6 text-muted_brown">
                  No article found. Email <a className="text-espresso underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or DM us on{" "}
                  <a className="text-espresso underline" href={INSTAGRAM_URL} target="_blank" rel="noreferrer">Instagram</a>.
                </div>
              )}
            </Accordion>
          ) : (
            <div className="space-y-8">
              {FAQ_TOPICS.map((topic, topicIndex) => (
                <article id={topic.id} key={topic.id} className="scroll-mt-28 bg-cream border border-line rounded-xl p-6 md:p-8">
                  <div className="mb-5">
                    <p className="overline mb-2">{String(topicIndex + 1).padStart(2, "0")}</p>
                    <h3 className="font-serif text-3xl text-espresso">{topic.title}</h3>
                    <p className="text-sm text-muted_brown mt-2">{topic.intro}</p>
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    {topic.items.map((f, i) => (
                      <AccordionItem key={f.q} value={`${topic.id}-${i}`} className="border-line">
                        <AccordionTrigger className="text-espresso text-left" data-testid={`help-${topic.id}-${i}`}>{f.q}</AccordionTrigger>
                        <AccordionContent className="text-muted_brown">{f.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <div className="mt-6 rounded-lg border border-line bg-ivory p-4 text-sm text-muted_brown">
                    Need extra help with {topic.title.toLowerCase()}? Email{" "}
                    <a className="text-espresso underline underline-offset-4" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{" "}
                    or DM us on{" "}
                    <a className="text-espresso underline underline-offset-4" href={INSTAGRAM_URL} target="_blank" rel="noreferrer">Instagram @note.console</a>.
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="container-nc py-16 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Mail, t: "Email us", d: SUPPORT_EMAIL, cta: "Send Email", href: `mailto:${SUPPORT_EMAIL}` },
          { icon: MessageCircle, t: "Instagram DM", d: "@note.console", cta: "Open Instagram", href: INSTAGRAM_URL },
          { icon: Phone, t: "Request a call", d: "Share your phone number and the best time to reach you", cta: "Request a Call", href: "#contact-form" },
        ].map((s) => (
          <a
            key={s.t}
            href={s.href}
            target={s.href.startsWith("http") ? "_blank" : undefined}
            rel={s.href.startsWith("http") ? "noreferrer" : undefined}
            className="bg-cream border border-line rounded-xl p-6 hover:shadow-hover transition-shadow"
          >
            <s.icon size={20} strokeWidth={1.5} className="text-gold mb-4" />
            <h4 className="font-serif text-xl text-espresso mb-1">{s.t}</h4>
            <p className="text-xs text-muted_brown">{s.d}</p>
            <div className="btn-ghost-gold mt-5">{s.cta}</div>
          </a>
        ))}
      </section>

      <section id="contact-form" className="scroll-mt-28 bg-ivory border-t border-line">
        <div className="container-nc py-20 max-w-3xl mx-auto">
          <p className="overline mb-4 text-center">Contact form</p>
          <h2 className="font-serif text-4xl text-espresso text-center mb-10">Send us a message</h2>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required placeholder="Full name" data-testid="contact-name" className="input-nc bg-cream" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            <input required type="email" placeholder="Email address" data-testid="contact-email" className="input-nc bg-cream" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
            <input placeholder="Phone (optional)" data-testid="contact-phone" className="input-nc bg-cream" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
            <input placeholder="Order number (optional)" data-testid="contact-order" className="input-nc bg-cream" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} />
            <select data-testid="contact-subject" className="input-nc bg-cream sm:col-span-2" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}>
              <option value="orders">Orders</option>
              <option value="shipping">Shipping</option>
              <option value="returns">Returns</option>
              <option value="product">Product question</option>
              <option value="other">Other</option>
            </select>
            <textarea required rows={5} placeholder="How can we help?" data-testid="contact-message" className="input-nc bg-cream sm:col-span-2" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} />
            <button data-testid="contact-submit" className="btn-primary sm:col-span-2">Send Message</button>
          </form>
        </div>
      </section>
    </div>
  );
}
